/**
 * Secure Cart Hook - All cart operations via backend Edge Functions
 * 
 * This hook handles:
 * - Cart state management
 * - Server-side price calculations
 * - Server-side stock validation
 * - Secure sale processing
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/context/SupabaseInventoryContext';
import { useNotifications } from '@/hooks/useNotifications';

export interface SecureCartItem {
  product: Product;
  quantity: number;
  calculatedPrice?: number; // Server-calculated price
}

export interface CartTotal {
  subtotal: number;
  total: number;
  itemCount: number;
}

export const useSecureCart = (companyId: string | undefined) => {
  const [cart, setCart] = useState<SecureCartItem[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [cartTotal, setCartTotal] = useState<CartTotal>({
    subtotal: 0,
    total: 0,
    itemCount: 0
  });
  const { success, error } = useNotifications();

  /**
   * Add product to cart with server-side validation
   */
  const addToCart = useCallback(async (product: Product, quantity: number = 1) => {
    if (!companyId) {
      error({
        title: "Erreur",
        description: "Aucune entreprise sélectionnée"
      });
      return false;
    }

    try {
      // Call backend to validate and add to cart
      const { data, error: apiError } = await supabase.functions.invoke('secure-cart-operations', {
        body: {
          action: 'add',
          companyId,
          productId: product.id,
          quantity
        }
      });

      if (apiError) throw apiError;

      if (!data.success) {
        error({
          title: "Erreur",
          description: data.error || "Impossible d'ajouter le produit"
        });
        return false;
      }

      // Update local cart state
      setCart(prevCart => {
        const existingItem = prevCart.find(item => item.product.id === product.id);
        if (existingItem) {
          return prevCart.map(item =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + quantity, calculatedPrice: data.item?.price }
              : item
          );
        } else {
          return [...prevCart, {
            product,
            quantity,
            calculatedPrice: data.item?.price
          }];
        }
      });

      success({
        title: "✅ Produit ajouté",
        description: `${product.name} ajouté au panier`
      });

      // Recalculate cart total
      await calculateCartTotal();

      return true;
    } catch (err) {
      console.error('Error adding to cart:', err);
      error({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Erreur lors de l'ajout au panier"
      });
      return false;
    }
  }, [companyId, success, error]);

  /**
   * Update cart item quantity with server-side validation
   */
  const updateQuantity = useCallback(async (productId: string, newQuantity: number) => {
    if (!companyId) return false;

    if (newQuantity <= 0) {
      return removeFromCart(productId);
    }

    try {
      const { data, error: apiError } = await supabase.functions.invoke('secure-cart-operations', {
        body: {
          action: 'update',
          companyId,
          productId,
          quantity: newQuantity
        }
      });

      if (apiError) throw apiError;

      if (!data.success) {
        error({
          title: "Erreur",
          description: data.error || "Stock insuffisant"
        });
        return false;
      }

      // Update local state
      setCart(prevCart =>
        prevCart.map(item =>
          item.product.id === productId
            ? { ...item, quantity: newQuantity, calculatedPrice: data.item?.price }
            : item
        )
      );

      await calculateCartTotal();
      return true;
    } catch (err) {
      console.error('Error updating quantity:', err);
      error({
        title: "Erreur",
        description: "Impossible de mettre à jour la quantité"
      });
      return false;
    }
  }, [companyId, error]);

  /**
   * Remove item from cart
   */
  const removeFromCart = useCallback(async (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
    await calculateCartTotal();
    return true;
  }, []);

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(() => {
    setCart([]);
    setCartTotal({ subtotal: 0, total: 0, itemCount: 0 });
  }, []);

  /**
   * Calculate cart total via backend (server-side pricing)
   */
  const calculateCartTotal = useCallback(async () => {
    if (!companyId || cart.length === 0) {
      setCartTotal({ subtotal: 0, total: 0, itemCount: 0 });
      return { subtotal: 0, total: 0, itemCount: 0 };
    }

    setIsCalculating(true);
    try {
      const cartItems = cart.map(item => ({
        productId: item.product.id,
        quantity: item.quantity
      }));

      const { data, error: apiError } = await supabase.functions.invoke('secure-pricing-engine', {
        body: {
          action: 'calculate',
          companyId,
          items: cartItems
        }
      });

      if (apiError) throw apiError;

      if (data.success) {
        const total = {
          subtotal: data.subtotal || 0,
          total: data.total || 0,
          itemCount: cart.reduce((sum, item) => sum + item.quantity, 0)
        };
        setCartTotal(total);
        return total;
      } else {
        throw new Error(data.error || 'Calculation failed');
      }
    } catch (err) {
      console.error('Error calculating cart total:', err);
      // Fallback to client-side calculation
      const clientTotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
      const total = {
        subtotal: clientTotal,
        total: clientTotal,
        itemCount: cart.reduce((sum, item) => sum + item.quantity, 0)
      };
      setCartTotal(total);
      return total;
    } finally {
      setIsCalculating(false);
    }
  }, [companyId, cart]);

  /**
   * Process sale via backend with secure validation
   */
  const processSale = useCallback(async (paymentMethod: 'Espèces' | 'Carte Bancaire' | 'Chèque' | 'Autre' = 'Espèces') => {
    if (!companyId) {
      error({
        title: "Erreur",
        description: "Aucune entreprise sélectionnée"
      });
      return false;
    }

    if (cart.length === 0) {
      error({
        title: "Panier vide",
        description: "Ajoutez des produits avant de valider"
      });
      return false;
    }

    try {
      const cartItems = cart.map(item => ({
        productId: item.product.id,
        quantity: item.quantity,
        productName: item.product.name
      }));

      const { data, error: apiError } = await supabase.functions.invoke('secure-sales-processing', {
        body: {
          action: 'process',
          companyId,
          items: cartItems,
          paymentMethod
        }
      });

      if (apiError) throw apiError;

      if (!data.success) {
        error({
          title: "Erreur de vente",
          description: data.error || "Impossible de traiter la vente"
        });
        return false;
      }

      success({
        title: "✅ Vente validée",
        description: "Tous les produits ont été vendus avec succès"
      });

      clearCart();
      return true;
    } catch (err) {
      console.error('Error processing sale:', err);
      error({
        title: "Erreur lors de la vente",
        description: err instanceof Error ? err.message : "Impossible de traiter la vente"
      });
      return false;
    }
  }, [companyId, cart, success, error, clearCart]);

  return {
    cart,
    cartTotal,
    isCalculating,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    calculateCartTotal,
    processSale
  };
};
